import {
  Box,
  Button,
  Card,
  Checkbox,
  Container,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@material-ui/core'
import AudiotrackIcon from '@mui/icons-material/Audiotrack'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { bulkDeleteMediaItems, deleteMediaItem } from 'src/api/media'
import CommonDialog from 'src/components/common/CommonDialog'
import ModalComponent from 'src/components/common/modalComponent'
import Toaster from 'src/components/helperComponents/Toaster'
import MediaUploadDialog from 'src/components/media/MediaUploadDialog'
import useQueryTable from 'src/hooks/useQueryTable'
import { LocalTable } from 'src/ui/Table'
import { getFileUrl } from 'src/util/helpers'
import { formatFileSize, getMediaFileName, MEDIA_LIBRARIES } from 'src/util/mediaConfig'

// Records store the file name only; the full CDN url is built here, on read.
const getMediaUrl = record => getFileUrl(getMediaFileName(record))

const copyToClipboard = async text => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }
  // http / older browsers fallback
  const input = document.createElement('textarea')
  input.value = text
  document.body.appendChild(input)
  input.select()
  document.execCommand('copy')
  document.body.removeChild(input)
}

const downloadFile = (url, filename) => {
  const link = document.createElement('a')
  link.href = url
  link.download = filename || ''
  link.target = '_blank'
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const PreviewCell = ({ record, library, onPreview }) => {
  if (library.key === 'images') {
    return (
      <img
        src={getMediaUrl(record)}
        alt={record.originalName}
        onClick={() => onPreview(record)}
        style={{
          width: 80,
          height: 50,
          objectFit: 'cover',
          borderRadius: 6,
          cursor: 'pointer',
        }}
      />
    )
  }
  const Icon = library.key === 'audio' ? AudiotrackIcon : PlayCircleOutlineIcon
  return (
    <IconButton color="primary" title={`Preview ${library.singular}`} onClick={() => onPreview(record)}>
      <Icon fontSize="large" />
    </IconButton>
  )
}

/**
 * Media library page — one component drives /app/media/images, /audio and
 * /videos. Server-side search/filter/pagination via useQueryTable, bulk
 * upload via MediaUploadDialog, inline preview/edit/delete/copy/download.
 */
const MediaLibraryList = ({ libraryKey }) => {
  const library = MEDIA_LIBRARIES[libraryKey]

  const [searchInput, setSearchInput] = useState('')
  const [searchText, setSearchText] = useState(null)
  const [extension, setExtension] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [isUploadOpen, setIsUploadOpen] = useState(false)
  const [previewRecord, setPreviewRecord] = useState(null)
  const [toast, setToast] = useState(null)
  const [selectedIds, setSelectedIds] = useState([])
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false)

  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  })

  // debounce the search box so we don't query on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => setSearchText(searchInput || null), 400)
    return () => clearTimeout(timer)
  }, [searchInput])

  const additionalFilters = useMemo(() => {
    if (!extension && !dateFrom && !dateTo) return null
    return { extension, dateFrom, dateTo }
  }, [extension, dateFrom, dateTo])

  const { data, isLoading, isFetching, refetch } = useQueryTable({
    name: `media/${library.key}`,
    searchText,
    pageIndex,
    pageSize,
    additionalFilters,
  })

  const showToast = (msg, severity = 'success') => setToast({ msg, severity })

  // Copies the file name exactly as stored — that is the value the other
  // modules (course, lesson, practice item) expect in their media fields.
  const handleCopyFileName = async record => {
    try {
      await copyToClipboard(getMediaFileName(record))
      showToast('File name copied to clipboard')
    } catch (err) {
      showToast('Could not copy the file name', 'error')
    }
  }

  const onDeleteRow = async id => {
    try {
      await deleteMediaItem(library.key, id)
      showToast(`${library.singular} deleted successfully`)
      setSelectedIds(prev => prev.filter(selectedId => selectedId !== id))
      refetch()
    } catch (err) {
      showToast(err?.response?.data?.message || `Could not delete this ${library.singular}`, 'error')
    }
  }

  // ---- bulk selection & delete ----
  const pageIds = useMemo(() => (data?.docs || []).map(doc => doc._id), [data?.docs])
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id))
  const somePageSelected = pageIds.some(id => selectedIds.includes(id))

  const toggleRowSelection = id =>
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(selectedId => selectedId !== id) : [...prev, id]
    )

  const togglePageSelection = () =>
    setSelectedIds(prev =>
      allPageSelected
        ? prev.filter(id => !pageIds.includes(id))
        : [...new Set([...prev, ...pageIds])]
    )

  const handleBulkDelete = async () => {
    setIsBulkDeleteOpen(false)
    try {
      const result = await bulkDeleteMediaItems(library.key, selectedIds)
      showToast(`${result?.deletedCount ?? selectedIds.length} ${library.label.toLowerCase()} deleted successfully`)
      setSelectedIds([])
      refetch()
    } catch (err) {
      showToast(err?.response?.data?.message || 'Could not delete the selected files', 'error')
    }
  }

  const columns = useMemo(
    () => [
      {
        id: 'select',
        enableSorting: false,
        width: 50,
        header: () => (
          <Checkbox
            size="small"
            sx={{ p: 0, color: '#fff', '&.Mui-checked': { color: '#fff' } }}
            checked={allPageSelected}
            indeterminate={!allPageSelected && somePageSelected}
            onClick={e => e.stopPropagation()}
            onChange={togglePageSelection}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            size="small"
            sx={{ p: 0 }}
            checked={selectedIds.includes(row.original._id)}
            onChange={() => toggleRowSelection(row.original._id)}
          />
        ),
      },
      {
        accessorKey: 'previewX',
        header: 'Preview',
        width: 110,
        // Renders a thumbnail/play button only — the File Name column already
        // carries this row's file name into the CSV.
        noExport: true,
        cell: ({ row }) => (
          <PreviewCell record={row.original} library={library} onPreview={setPreviewRecord} />
        ),
      },
      {
        accessorKey: 'nameX',
        header: 'Name',
        width: 240,
        accessorFn: row => row?.originalName,
      },
      {
        accessorKey: 'extension',
        header: 'Type',
        width: 90,
        accessorFn: row => (row?.extension || '').toUpperCase(),
      },
      {
        accessorKey: 'fileSizeX',
        header: 'Size',
        width: 110,
        accessorFn: row => formatFileSize(row?.fileSize),
      },
      {
        accessorKey: 'createdAtX',
        header: 'Uploaded On',
        width: 150,
        accessorFn: row => moment(row?.createdAt).format('DD/MM/YYYY HH:mm'),
      },
      {
        accessorKey: 'fileNameX',
        header: 'File Name',
        width: 120,
        exportValue: row => getMediaFileName(row),
        cell: ({ row }) => (
          <Stack direction="row">
            <IconButton
              size="small"
              title="Copy File Name"
              onClick={() => handleCopyFileName(row.original)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              title="Download"
              onClick={() => downloadFile(getMediaUrl(row.original), row.original.originalName)}
            >
              <FileDownloadIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [library, selectedIds, pageIds]
  )

  return (
    <>
      <Helmet>
        <title>{`Media ${library.label} | Langistan`}</title>
      </Helmet>
      <Box sx={{ backgroundColor: 'background.default', minHeight: '100%', py: 3 }}>
        <Container maxWidth={false}>
          {/* Toolbar: search, filters, upload */}
          <Card sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label={`Search ${library.label.toLowerCase()}…`}
                  placeholder="Name, extension or MIME type"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                />
              </Grid>
              <Grid item xs={6} sm={2} md={2}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="File Type"
                  value={extension}
                  onChange={e => setExtension(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {library.extensions.map(ext => (
                    <MenuItem key={ext} value={ext}>
                      .{ext}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={2} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="From"
                  InputLabelProps={{ shrink: true }}
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                />
              </Grid>
              <Grid item xs={6} sm={2} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="To"
                  InputLabelProps={{ shrink: true }}
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                />
              </Grid>
              <Grid item xs={6} sm={2} md={3} sx={{ textAlign: 'right' }}>
                <Button variant="contained" color="primary" onClick={() => setIsUploadOpen(true)}>
                  + Upload {library.label}
                </Button>
              </Grid>
            </Grid>
          </Card>

          {/* Media table */}
          <Box sx={{ pt: 3 }}>
            <LocalTable
              columns={columns}
              onDeleteClick={onDeleteRow}
              data={data}
              name={library.singular}
              isLoading={isLoading}
              isFetching={isFetching}
              setPagination={setPagination}
              deleteActionBtnTitle={`Delete ${library.singular}`}
              viewActionBtnTitle={`Preview ${library.singular}`}
              onViewActionBtnClick={setPreviewRecord}
              exportApiName={`media/${library.key}`}
              exportFileName={`media-${library.key}.csv`}
              toolbarExtra={
                selectedIds.length > 0 && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => setIsBulkDeleteOpen(true)}
                  >
                    Delete ({selectedIds.length})
                  </Button>
                )
              }
            />
          </Box>

          {/* Bulk upload dialog */}
          <CommonDialog
            open={isUploadOpen}
            title={`Bulk Upload ${library.label}`}
            onClose={() => setIsUploadOpen(false)}
            maxWidth="sm"
          >
            {isUploadOpen && (
              <MediaUploadDialog
                library={library}
                refetch={refetch}
                onClose={() => setIsUploadOpen(false)}
              />
            )}
          </CommonDialog>

          {/* Preview dialog */}
          <CommonDialog
            open={!!previewRecord}
            title={previewRecord?.originalName}
            onClose={() => setPreviewRecord(null)}
            maxWidth={library.key === 'videos' ? 'md' : 'sm'}
          >
            {previewRecord && (
              <Stack spacing={2} alignItems="center">
                {library.key === 'images' && (
                  <img
                    src={getMediaUrl(previewRecord)}
                    alt={previewRecord.originalName}
                    style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }}
                  />
                )}
                {library.key === 'audio' && (
                  <audio controls src={getMediaUrl(previewRecord)} style={{ width: '100%' }} />
                )}
                {library.key === 'videos' && (
                  <video
                    controls
                    src={getMediaUrl(previewRecord)}
                    style={{ width: '100%', maxHeight: '70vh', borderRadius: 8 }}
                  />
                )}
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(previewRecord.fileSize)} — uploaded{' '}
                  {moment(previewRecord.createdAt).format('DD/MM/YYYY HH:mm')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  {getMediaFileName(previewRecord)}
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleCopyFileName(previewRecord)}
                  >
                    Copy File Name
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => downloadFile(getMediaUrl(previewRecord), previewRecord.originalName)}
                  >
                    Download
                  </Button>
                </Stack>
              </Stack>
            )}
          </CommonDialog>

          {/* Bulk delete confirmation */}
          {isBulkDeleteOpen && (
            <ModalComponent
              open={isBulkDeleteOpen}
              onClose={() => setIsBulkDeleteOpen(false)}
              onConfirm={handleBulkDelete}
              title={`Are you sure, you want to delete ${selectedIds.length} selected ${library.label.toLowerCase()}?`}
              confirmBtn="YES, Delete"
            />
          )}

          {toast && <Toaster msg={toast.msg} severity={toast.severity} onClose={() => setToast(null)} />}
        </Container>
      </Box>
    </>
  )
}

export default MediaLibraryList
